import { Alert, Button, Grid2 as Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { LoadingSection, MyPaper, SectionPaper } from "../../../components";
import { ExchangeData, useCalculateCostingMutation, useLazyGetBOMTreeByIdQuery, useUpdatePricingMutation } from "../../../redux/services";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { QueryType, StyledDataGrid } from "../../workbook/actions/actionTable";
import { closeDialog, openDialog } from "../../../redux/slices/dialog";
import { ListingAvailableCosts } from "../components/ListingAvailableCosts";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import moment from "moment";
import { Costing, CustomCost, formatCurrency } from "pdf-generator";
import { ChildProps } from "..";
import { TypeOptions } from "../summary/editSummary";
import { CustomCostColumn } from "./editCosting";
import Chart from "react-google-charts";
import { chartOptions, defaultCostingSummary, findFirstCustomCosts, pricingOutdatedUpdateFn } from "../utils";
import { HistoryRounded } from "@mui/icons-material";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { getUserSelectedCurrency } from "../../../utils";

export const FlatView: React.FC<ChildProps & { costingRes: QueryType }> = ({ refetchCosting, refetch, costingData, costingRes, readOnly }) => {
    const { costing, hasCosting, levelsData = [] } = costingData;
    const { data = [], isFetching, isLoading, isError, isSuccess } = costingRes;

    const commonColumns: GridColDef[] = [
        {
            field: 'material',
            width: 200,
            headerName: 'Material ID',
            renderCell: (params: GridRenderCellParams) => {
                return <Stack>
                    <Typography
                        component="div"
                        variant="body2"
                    >
                        {params.row.material}
                    </Typography>
                </Stack>
            }
        },
        {
            field: 'short_description',
            width: 320,
            headerName: 'Description',
        },
        {
            field: 'material_cost',
            headerName: 'Material Cost',
            width: 150,
            renderHeader: (params: GridColumnHeaderParams) => {
                return (
                    <Tooltip title={"Material Cost"}>
                        <Stack width={"100%"} alignItems={"flex-end"}>
                            <Typography variant="body2"><strong>Material Cost</strong></Typography>
                        </Stack>
                    </Tooltip>
                );
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography
                    variant="body2"
                >
                    {isNaN(params.row?.material_cost) ? "" : formatCurrency(Number(params.row?.material_cost?.toFixed(2)), params?.row?.material_currency, false, false) || ''}
                </Typography>
            },
            type: "number",
            align: 'right',
        },
        {
            field: 'material_currency',
            headerName: 'Material Currency',
            width: 150
        },
        {
            field: 'override_cost',
            headerName: 'Override Cost',
            width: 150,
            renderHeader: (params: GridColumnHeaderParams) => {
                return (
                    <Tooltip title={"Override Cost"}>
                        <Stack width={"100%"} alignItems={"flex-end"}>
                            <Typography variant="body2"><strong>Override Cost</strong></Typography>
                        </Stack>
                    </Tooltip>
                );
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography
                    variant="body2"
                >
                    {(params?.row?.id === costing?.calculated_cost?._id || isNaN(params.row?.override_cost)) ? "" : formatCurrency(Number(params.row?.override_cost?.toFixed(2)), params?.row?.override_currency, false, false) || ''}
                </Typography>
            },
            type: "number",
            align: 'right',
        },
        {
            field: 'override_currency',
            headerName: 'Override Currency',
            width: 150,
        },
        {
            field: 'spare_part_quantity',
            headerName: 'Spare Part Qty',
            width: 150,
            renderHeader: (params: GridColumnHeaderParams) => {
                return (
                    <Tooltip title={"Spare Part Qty"}>
                        <Stack width={"100%"} alignItems={"flex-end"}>
                            <Typography variant="body2"><strong>Spare Part Qty</strong></Typography>
                        </Stack>
                    </Tooltip>
                );
            },
            type: "number",
            align: 'right',
        },
        {
            field: 'normal_quantity',
            headerName: 'Quantity',
            width: 150,
            renderHeader: (params: GridColumnHeaderParams) => {
                return (
                    <Tooltip title={"Quantity"}>
                        <Stack width={"100%"} alignItems={"flex-end"}>
                            <Typography variant="body2"><strong>Quantity</strong></Typography>
                        </Stack>
                    </Tooltip>
                );
            },
            type: "number",
            align: 'right',
        },
        {
            field: 'total_quantity',
            headerName: 'Total Quantity',
            width: 150,
            renderHeader: (params: GridColumnHeaderParams) => {
                return (
                    <Tooltip title={"Total Quantity"}>
                        <Stack width={"100%"} alignItems={"flex-end"}>
                            <Typography variant="body2"><strong>Total Quantity</strong></Typography>
                        </Stack>
                    </Tooltip>
                );
            },
            type: "number",
            align: 'right',
        },
        {
            field: 'total_cost',
            headerName: 'Total Cost',
            width: 150,
            renderHeader: (params: GridColumnHeaderParams) => {
                return (
                    <Tooltip title={"Total Cost"}>
                        <Stack width={"100%"} alignItems={"flex-end"}>
                            <Typography variant="body2"><strong>Total Cost</strong></Typography>
                        </Stack>
                    </Tooltip>
                );
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography
                    variant="body2"
                >
                    {isNaN(params.row?.total_cost) ? "" : formatCurrency(Number(params.row?.total_cost?.toFixed(2)), params?.row?.total_cost_currency, false, false) || ''}
                </Typography>
            },
            type: "number",
            align: 'right',
        },
        {
            field: 'total_cost_currency',
            headerName: 'Total Cost Currency',
            width: 180,
        },
        {
            field: 'markup_percentage',
            headerName: 'Markup in (%)',
            width: 150,
            type: 'number',
            headerClassName: 'highlight-table-row',
            valueGetter: (value, row, column, apiRef) => `${row?.markup_percentage || 0}%`,
        },
        {
            field: 'cost_in_bom_currency',
            headerName: "New Total Cost (BOM Currency)",
            renderHeader: (params: GridColumnHeaderParams) => {
                return (
                    <Tooltip title={"New Total Cost in BOM Currency"}>
                        <Stack width={"100%"} alignItems={"flex-end"}>
                            <Typography variant="body2"><strong>New Total Cost</strong></Typography>
                            <Typography variant={"caption"}>(BOM Currency)</Typography>
                        </Stack>
                    </Tooltip>
                );
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography
                    variant="body2"
                >
                    {isNaN(params.row?.cost_in_bom_currency) ? '' : formatCurrency(Number(params.row?.cost_in_bom_currency?.toFixed(2)), params?.row?.bom_currency, false, false)}
                </Typography>
            },
            width: 150,
            type: "number",
            align: 'right',
            sortable: false,
            hideSortIcons: true,
            disableColumnMenu: true,
        },
        {
            field: 'bom_currency',
            headerName: 'BOM Currency',
            width: 180,
            sortable: false,
            hideSortIcons: true,
            disableColumnMenu: true,
        },
        {
            field: 'unit_cost_in_pricing_currency',
            headerName: "New Unit Price",
            renderHeader: (params: GridColumnHeaderParams) => {
                return (
                    <Tooltip title={"New Unit Price in Pricing Currency"}>
                        <Stack width={"100%"} alignItems={"flex-end"}>
                            <Typography variant="body2"><strong>New Unit Price</strong></Typography>
                            <Typography variant={"caption"}>(Pricing Currency)</Typography>
                        </Stack>
                    </Tooltip>
                );
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography
                    variant="body2"
                >
                    {isNaN(params.row?.unit_cost_in_pricing_currency) ? '' : formatCurrency(Number(params.row?.unit_cost_in_pricing_currency?.toFixed(2)), params?.row?.total_currency, false, false)}
                </Typography>
            },
            width: 150,
            type: "number",
            align: 'right',
        },
        {
            field: 'markup_cost',
            headerName: "New Total Cost",
            renderHeader: (params: GridColumnHeaderParams) => {
                return (
                    <Tooltip title={"New Total Cost in Pricing Currency"}>
                        <Stack width={"100%"} alignItems={"flex-end"}>
                            <Typography variant="body2"><strong>New Total Cost</strong></Typography>
                            <Typography variant={"caption"}>(Pricing Currency)</Typography>
                        </Stack>
                    </Tooltip>
                );
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography
                    variant="body2"
                >
                    {isNaN(params.row?.markup_cost) ? '' : formatCurrency(Number(params.row?.markup_cost?.toFixed(2)), params?.row?.total_currency, false, false)}
                </Typography>
            },
            width: 150,
            type: "number",
            align: 'right',
        },
        {
            field: 'total_currency',
            headerName: 'Currency',
            width: 150
        },
    ];

    const { id = "" } = useParams();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const { pricing, auth } = useAppSelector(store => store);

    const DEFAULT_CURRENCY = getUserSelectedCurrency(auth.userPreferences).value;

    const [calculateCost] = useCalculateCostingMutation();
    const [getBOMTree] = useLazyGetBOMTreeByIdQuery();
    const [updatePricing] = useUpdatePricingMutation();

    const [calculatedRows, setCalculatedRows] = React.useState<any[]>([]);
    const [columns, setColumns] = React.useState<GridColDef[]>(commonColumns);

    const [bomCostingDetails, setDetails] = React.useState(defaultCostingSummary);

    const appendChild = (data: any, row: any[]): any[] => {
        if (data?.children_costs?.length === 0 && data?.material_costs?.length === 0) {
            return row;
        }

        data?.children_costs?.forEach((bom_children: any) => {
            // Appending Level
            row.concat(appendChild(bom_children, row));
        });

        data?.material_costs?.forEach((bom_material: any, bom_material_index: number) => {

            let pathId = bom_material.sequence_id + "-r" + bom_material?.version;

            const obj = bom_material?.custom_costs?.reduce((acc: object, cur: { description: string }) => ({ ...acc, [`custom_${cur.description}`]: cur }), {});

            row.push({
                id: bom_material?._id + bom_material_index + Math.random(),
                material: pathId,
                short_description: bom_material?.short_description,
                material_cost: bom_material?.material_cost?.cost ?? 0,
                material_currency: `${bom_material?.material_cost?.currency ?? DEFAULT_CURRENCY}`,
                override_cost: bom_material?.override_cost ?? 0,
                override_currency: `${bom_material?.material_cost?.currency ?? DEFAULT_CURRENCY}`,
                spare_part_quantity: bom_material?.spare_part_quantity ?? 0,
                normal_quantity: bom_material.normal_quantity ?? 1,
                total_quantity: bom_material.total_quantity ?? 1,
                total_cost: bom_material.total_cost?.cost ?? 0,
                total_cost_currency: bom_material?.total_cost?.currency ?? DEFAULT_CURRENCY,
                markup_percentage: bom_material?.markup_percentage ?? 0,
                cost_in_bom_currency: bom_material?.final_total_cost_in_parent_currency?.cost ?? 0,
                bom_currency: `${bom_material?.final_total_cost_in_parent_currency?.currency ?? DEFAULT_CURRENCY}`,
                markup_cost: (bom_material?.final_total_cost_in_pricing_currency?.cost ?? 0),
                total_currency: `${bom_material.final_total_cost_in_pricing_currency?.currency ?? DEFAULT_CURRENCY}`,
                unit_cost_in_pricing_currency: (bom_material?.unit_cost_in_pricing_currency?.cost ?? 0),
                child_id: bom_material?.child_id,
                ...obj
            });
        });

        return row;
    };

    const massageRows = React.useCallback((res: any) => {
        if (Object.keys(res).length <= 0) {
            setCalculatedRows([]);
            return;
        }

        setCalculatedRows(appendChild(res, []));
        // eslint-disable-next-line
    }, [costing?.calculated_cost]);

    const onSelect = (costing: Costing) => {
        dispatch(closeDialog());
        massageRows(costing?.calculated_cost);
        const base_currency = costing.pricing_config?.exchange_rates?.base_currency ?? DEFAULT_CURRENCY;
        setDetails([
            {
                label: `Cost Breakup for: `,
                value: `${costing?.calculated_cost?.short_description} (${costing?.calculated_cost?.sequence_id ?? ""
                    }-r${costing?.calculated_cost?.version ?? 0})`,
            },
            {
                label: `Total Cost (after tax): `,
                value: `${formatCurrency(costing?.final_price ?? 0, base_currency, false)}`,
            },
            {
                label: `Calculated By: `,
                value: `${costing?.created_user}`,
            },
            {
                label: `Calculated At: `,
                value: `${moment(costing?.created_time).format("lll")}`,
            },
            {
                label: `Pricing ID: `,
                value: pricing?.sequenceId ?? "",
            }
        ]);
        const arr = costing?.calculated_cost ? (findFirstCustomCosts(costing.calculated_cost) ?? []) : [];
        let cols = arr?.map((cur: CustomCost): GridColDef => ({
            field: `custom_${cur.description}`,
            width: 150,
            sortable: false,
            hideSortIcons: true,
            disableColumnMenu: true,
            renderHeader: (params: GridColumnHeaderParams) => {
                const type = TypeOptions.find(t => cur?.type === t?.value)?.label ?? '';
                return <CustomCostColumn
                    tooltip_title={`${cur.description} ${cur?.subtract === true ? `(${type}-Subtract)` : `(${type}-Add)`}`}
                    col_name={cur.description}
                />;
            },
            type: 'number',
            headerClassName: 'highlight-table-row',
            valueGetter: (value, row, column, apiRef) => row?.[`custom_${cur.description}`]?.value,
        })) ?? [];
        setColumns([...commonColumns.slice(0, commonColumns.length - 6), ...cols, ...commonColumns.slice(commonColumns.length - 6, commonColumns.length)]);
    }

    const showCalculatedCostings = (costings: Costing[]) => {
        dispatch(openDialog({
            title: "",
            body: <ListingAvailableCosts readOnly={readOnly} costings={costings} onSelect={(costing) => onSelect(costing)} refetchCosting={refetchCosting} />,
            hideNegativeBtn: true, hidePositiveBtn: true, enablePadding: false
        }));
    }

    const onCalculate = async (exchange_rates: ExchangeData) => {
        dispatch(openBackdrop("Calculating Cost..."));
        await calculateCost({ pricing_id: id, exchange_rates: exchange_rates })
            .unwrap()
            .then(() => {
                refetchCosting();
            })
            .catch((error: any) => {
                if (error?.data?.title === "BOM Outdated" && error?.data?.status === 409) {
                    dispatch(openConfirmationDialog({
                        title: "Pricing Update",
                        body: "The BOM of this pricing is updated, do you want to update the pricing with latest BOM changes ?",
                        negativeBtn: "Cancel",
                        positiveBtn: "Yes",
                        onOk: () => {
                            pricingOutdatedUpdateFn(getBOMTree, updatePricing, dispatch, pricing, id, () => {
                                if (refetch) {
                                    refetch();
                                }
                                refetchCosting();
                            });
                        },
                        onNegativeBtn: () => dispatch(closeConfirmationDialog())
                    }))
                }
                enqueueSnackbar(error?.data?.title ?? "Unable to calculate the pricing", { variant: "error" });
                console.error(`Error calculating pricing: ${JSON.stringify(error)}`)
            }).finally(() => {
                dispatch(closeBackdrop());
            });
    }

    useEffect(() => {
        if (Object.keys(costing || {}).length > 0) {
            massageRows(costing?.calculated_cost || {});
            const base_currency = costing.pricing_config?.exchange_rates?.base_currency ?? DEFAULT_CURRENCY;
            setDetails([
                {
                    label: `Cost Breakup for: `,
                    value: `${costing?.calculated_cost?.short_description} (${costing?.calculated_cost?.sequence_id ?? ""
                        }-r${costing?.calculated_cost?.version ?? 0})`,
                },
                {
                    label: `Total Cost (after tax): `,
                    value: `${formatCurrency(costing?.final_price ?? 0, base_currency, false)}`,
                },
                {
                    label: `Calculated By: `,
                    value: `${costing?.created_user}`,
                },
                {
                    label: `Calculated At: `,
                    value: `${moment(costing?.created_time).format("lll")}`,
                },
                {
                    label: `Pricing ID: `,
                    value: pricing?.sequenceId ?? "",
                }
            ]);

            const arr = costing?.calculated_cost ? (findFirstCustomCosts(costing.calculated_cost) ?? []) : [];
            let cols = arr?.map((cur: CustomCost): GridColDef => ({
                field: `custom_${cur.description}`,
                width: 150,
                renderHeader: (params: GridColumnHeaderParams) => {
                    const type = TypeOptions.find(t => cur?.type === t?.value)?.label ?? '';
                    return <CustomCostColumn
                        tooltip_title={`${cur.description} ${cur?.subtract === true ? `(${type}-Subtract)` : `(${type}-Add)`}`}
                        col_name={cur.description}
                    />;
                },
                type: 'number',
                headerClassName: 'highlight-table-row',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.[`custom_${cur.description}`]?.value,
            })) ?? [];
            setColumns([...commonColumns.slice(0, commonColumns.length - 6), ...cols, ...commonColumns.slice(commonColumns.length - 6, commonColumns.length)]);
        } else {
            setCalculatedRows([]);
            setDetails(defaultCostingSummary);
        }
        // eslint-disable-next-line
    }, [JSON.stringify(costing), costingRes.status, DEFAULT_CURRENCY]);

    return (
        (<MyPaper height={"100%"}>
            <Stack
                sx={{
                    gap: 2,
                    height: "100%",
                    overflow: "hidden"
                }}>
                <>
                    {!hasCosting && !isLoading && (
                        <Alert
                            severity="warning"
                            action={
                                <Button variant="outlined" color="primary" size="small" onClick={() => onCalculate(pricing?.exchange_rates)}>
                                    Calculate
                                </Button>
                            }
                        >
                            Costing yet to be calculated for the Pricing
                        </Alert>
                    )}

                    {isLoading && <LoadingSection message="Fetching BOM Cost Breakup" />}

                    {isError && <Alert severity="error">Something went wrong, Unable to fetch costing details</Alert>}

                    {(isSuccess && Object.values(data ? data : {}).length > 0) &&
                        <>
                            {hasCosting &&
                                <Stack>
                                    <SectionPaper stack isExpand={false} title={"Costing Details"} renderButtons={[
                                        <Tooltip title="Costing History">
                                            <IconButton onClick={() => showCalculatedCostings(data ?? [])}
                                            >
                                                <HistoryRounded color="primary" />
                                            </IconButton>
                                        </Tooltip>
                                    ]}>
                                        <Grid container alignItems={"center"}>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Stack gap={1}>
                                                    {bomCostingDetails.map(detail => (<Stack gap={1} direction={{ sm: 'column', md: "row" }}>
                                                        <Typography variant="body2">{detail.label}</Typography>
                                                        <Typography variant="body2"><b>{detail.value}</b></Typography>
                                                    </Stack>))}
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Chart
                                                    chartType="PieChart"
                                                    data={levelsData}
                                                    options={{
                                                        ...chartOptions,
                                                        textColor: 'red',
                                                        title: `BOM First Level Details (${costing?.pricing_config?.exchange_rates?.base_currency})`,
                                                    }}
                                                    width={"100%"}
                                                    height={"250px"}
                                                />
                                            </Grid>
                                        </Grid>
                                    </SectionPaper>
                                </Stack>
                            }

                            <Stack
                                sx={{
                                    flexGrow: 1,
                                    overflow: "auto"
                                }}>
                                <StyledDataGrid
                                    saveLocal={false}
                                    density={'compact'}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                override_currency: false,
                                            },
                                        },
                                        pinnedColumns: {
                                            left: ["material", "short_description"]
                                        },
                                    }}
                                    sx={{
                                        "& .MuiDataGrid-row:hover": {
                                            backgroundColor: "white",
                                        },
                                        '.MuiDataGrid-virtualScroller': {
                                            overflow: 'auto !important',
                                            height: "auto !important"
                                        },
                                        '.MuiDataGrid-columnHeaderTitleContainer': {
                                            width: "100% !important",
                                        },
                                        '.MuiDataGrid-columnHeaderTitleContainerContent': {
                                            width: "100% !important",
                                        },
                                        "& .MuiInputBase-root": {
                                            border: "1px solid rgba(224, 224, 224, 1)",
                                            borderRadius: 1,
                                            px: 1,
                                            py: 0,
                                        },
                                        '.MuiDataGrid-cell--textRight': {
                                            // justifyContent: 'flex-start !important'
                                        }
                                    }}
                                    showToolbar={true}
                                    loading={isFetching || isLoading}
                                    rows={calculatedRows}
                                    columns={columns}
                                    disableChildrenFiltering
                                    getRowId={(row) => row?.id}
                                    hideFooter={true}
                                    pagination={false}
                                />
                            </Stack>
                        </>
                    }
                </>
            </Stack>
        </MyPaper>)
    );
}